import React, { useState, useMemo } from 'react';
import Table from '../../components/Table/Table';
import BankForm from '../../components/Form/BankForm';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { Bank, TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import { useCurrencies } from '../../hooks/useCurrencies';
import {
  useBanks,
  useCreateBank,
  useUpdateBank,
  useDeleteBank,
} from '../../hooks/useBanks';

const Banks: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingBank, setEditingBank] = useState<Bank | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState<string>('all');
  const [balanceFromFilter, setBalanceFromFilter] = useState<string>('');
  const [balanceToFilter, setBalanceToFilter] = useState<string>('');

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: banks, isLoading, error, refetch } = useBanks(queryOptions);
  const { data: currencies = [] } = useCurrencies();

  const createMutation = useCreateBank(() => {
    setShowForm(false);
    setEditingBank(undefined);
    setTimeout(() => refetch(), 100);
  });

  const updateMutation = useUpdateBank(() => {
    setShowForm(false);
    setEditingBank(undefined);
    setTimeout(() => refetch(), 100);
  });

  const deleteMutation = useDeleteBank(() => {
    setTimeout(() => refetch(), 100);
  });

  const filteredBanks = useMemo(() => {
    return banks.filter(bank => {
      const matchesSearch = !searchTerm || (
        bank.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bank.accountNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bank.accountType.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && bank.isActive) ||
        (statusFilter === 'inactive' && !bank.isActive);
      const matchesCurrency = currencyFilter === 'all' || resolveCurrencyCode(bank.currency, currencies) === currencyFilter;
      const matchesAccountType = accountTypeFilter === 'all' || bank.accountType === accountTypeFilter;
      const matchesBalanceFrom = !balanceFromFilter || bank.balance >= parseFloat(balanceFromFilter);
      const matchesBalanceTo = !balanceToFilter || bank.balance <= parseFloat(balanceToFilter);
      return matchesSearch && matchesStatus && matchesCurrency &&
        matchesAccountType && matchesBalanceFrom && matchesBalanceTo;
    });
  }, [banks, currencies, searchTerm, statusFilter, currencyFilter, accountTypeFilter, balanceFromFilter, balanceToFilter]);

  const handleExport = () => {
    const exportData = filteredBanks.map(bank => ({
      'Bank Name': bank.name,
      'Account Number': bank.accountNumber,
      'Account Type': bank.accountType,
      'Balance': bank.balance,
      'Currency': resolveCurrencyCode(bank.currency, currencies),
      'Status': bank.isActive ? 'Active' : 'Inactive',
      'Created': new Date(bank.createdAt).toLocaleDateString()
    }));
    exportToExcel(exportData, `bank_accounts_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredBanks.map(bank => ({
      'Bank Name': bank.name,
      'Account Number': bank.accountNumber,
      'Account Type': bank.accountType,
      'Balance': bank.balance,
      'Currency': resolveCurrencyCode(bank.currency, currencies),
      'Status': bank.isActive ? 'Active' : 'Inactive',
      'Created': new Date(bank.createdAt).toLocaleDateString()
    }));
    printAsExcel(exportData, 'Bank Accounts');
  };

  const handleExportJSON = () => {
    const dataStr = JSON.stringify(filteredBanks, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bank_accounts_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCurrencyFilter('all');
    setAccountTypeFilter('all');
    setBalanceFromFilter('');
    setBalanceToFilter('');
  };

  const filterConfigs: FilterConfig[] = [
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [{ value: 'all', label: 'All Statuses' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }] },
    { key: 'currency', label: 'Currency', type: 'select', value: currencyFilter, onChange: setCurrencyFilter, options: [
      { value: 'all', label: 'All Currencies' },
      ...(currencies as any[])
        .filter((c: any) => c.isActive !== false && c.code)
        .map((c: any) => ({ value: c.code, label: c.code }))
    ] },
    { key: 'accountType', label: 'Account Type', type: 'select', value: accountTypeFilter, onChange: setAccountTypeFilter, options: [{ value: 'all', label: 'All Types' }, { value: 'checking', label: 'Checking' }, { value: 'savings', label: 'Savings' }, { value: 'business', label: 'Business' }, { value: 'foreign', label: 'Foreign Currency' }] },
    { key: 'balanceFrom', label: 'Min Balance', type: 'number', value: balanceFromFilter, onChange: setBalanceFromFilter, placeholder: '0.00', step: '0.01' },
    { key: 'balanceTo', label: 'Max Balance', type: 'number', value: balanceToFilter, onChange: setBalanceToFilter, placeholder: '999999.99', step: '0.01' },
  ];

  const columns: TableColumn[] = [
    { key: 'name', label: 'Bank Name', sortable: true },
    { key: 'accountNumber', label: 'Account Number', sortable: true },
    { key: 'accountType', label: 'Account Type', sortable: true },
    { key: 'balance', label: 'Balance', sortable: true, render: (value: number, row: any) => {
      const currency = resolveCurrencyCode(row.currency, currencies);
      const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;
      return `${symbol} ${value.toLocaleString()}`;
    }},
    { key: 'currency', label: 'Currency', sortable: true, render: (value: any) => resolveCurrencyCode(value, currencies) },
    { key: 'isActive', label: 'Status', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {value ? 'Active' : 'Inactive'}
      </span>
    )},
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
  ];

  const handleEdit = (bank: Bank) => {
    setEditingBank(bank);
    setShowForm(true);
  };

  const handleDelete = async (bank: Bank) => {
    if (!window.confirm(`Are you sure you want to delete bank account "${bank.name}"?`)) return;
    if (!bank?.id) return;
    try {
      await deleteMutation.execute(bank);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleAdd = () => {
    setEditingBank(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<Bank>) => {
    try {
      if (editingBank?.id) {
        await updateMutation.execute({ id: editingBank.id, data });
      } else {
        await createMutation.execute({
          name: data.name,
          accountNumber: data.accountNumber,
          accountType: data.accountType,
          balance: data.balance || 0,
          currency: typeof data.currency === 'object' ? (data.currency as any)?.documentId || (data.currency as any)?.id : data.currency,
          isActive: data.isActive !== undefined ? data.isActive : true,
        });
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingBank(undefined);
  };

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} entityName="banks" />;

  if (showForm) {
    return (
      <div className="space-y-6">
        <BankForm bank={editingBank} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bank Accounts"
        description="Manage your company's bank accounts and financial institutions"
        actions={[{ label: 'Export JSON', onClick: handleExportJSON, variant: 'info' }]}
      />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search bank accounts..."
        filters={filterConfigs}
        onClearFilters={handleClearFilters}
        totalCount={banks.length}
        filteredCount={filteredBanks.length}
        entityName="Bank Accounts"
        onAdd={handleAdd}
        addLabel="Add Bank Account"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <Table
        columns={columns}
        data={filteredBanks}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        title=""
        searchPlaceholder=""
        searchable={false}
      />
    </div>
  );
};

export default Banks;
